import React, { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useSwipeable } from "react-swipeable";
import useKeypress from "react-use-keypress";
import axios from "axios";
import { base_url } from "constant/constant";
import galleryArrowLeft from "images/gallery/gallery-arrow-left.svg";
import galleryArrowRight from "images/gallery/gallery-arrow-right.svg";
import pinIconWhite from "images/gallery/pin-icon-white.svg";
import getImageVariantUrl from "utils/getImageVariantUrl";

// ─── Types ───────────────────────────────────────────────────────────────────

interface RoomCategory {
  title: string;
  images: string[];
  amenities?: { name: string; value?: string[] }[];
}

interface FlatImage {
  url: string;
  roomTitle: string;
  roomDescription?: string;
}

export interface PhotoGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialIndex: number;
  propertyTitle: string;
  propertyLocation: string;
  propertyId: string;
  carouselImages: string[];
  amenities?: { name: string; value?: string[] }[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────


const flattenCategories = (categories: RoomCategory[]): FlatImage[] =>
  categories.flatMap((cat) => {
    const roomDescription = formatAmenitiesSummary(cat.amenities);
    return cat.images.map((url) => ({ url, roomTitle: cat.title, roomDescription }));
  });

const formatAmenitiesSummary = (amenities?: { name: string; value?: string[] }[]): string => {
  if (!amenities || amenities.length === 0) return "";
  return amenities
    .filter((a) => a && a.name)
    .map((a) => a.name)
    .join(", ");
};

// ─── Component ───────────────────────────────────────────────────────────────

const PhotoGalleryModal: FC<PhotoGalleryModalProps> = ({
  isOpen,
  onClose,
  initialIndex,
  propertyTitle,
  propertyLocation,
  propertyId,
  carouselImages,
  amenities,
}) => {
  // ── State ────────────────────────────────────────────────────────────────

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [categories, setCategories] = useState<RoomCategory[]>([]);

  // The Figma frame is 916x610 (~3:2). On anything smaller than a desktop that
  // overflows the viewport, so the frame is sized to the viewport while keeping
  // the ratio — width capped at 92vw, height capped at 70vh — and the carousel
  // maths below reads these live values instead of the fixed constants.
  // Measured height of the header (title + location + however many rows the
  // category pills wrap into). The pill count varies per property, so the image
  // top can't be a fixed guess — we read the real height and push the image
  // below it. Falls back to a sensible default until the first measurement.
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [categories]);

  const [dims, setDims] = useState({ w: 916, h: 610, top: 140, vw: 916 });
  useEffect(() => {
    const RATIO = 610 / 916;
    const compute = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let w = Math.min(916, vw * 0.92);
      // Vertical budget: `top` reserves room for the header (the pills can wrap
      // to several rows, so we use its measured height + a gap instead of a flat
      // guess), and `bottomClearance` reserves room for the "Back" pill below
      // the image. The image's height is capped to whatever's left, so
      // top + height + bottomClearance never exceeds the viewport — unlike
      // centering on a percentage of viewport height, which had no such floor
      // and let the image (and everything below it) run past the header on short
      // viewports, or past the bottom of the screen entirely once a minimum top
      // was enforced.
      const compactNow = w < 700;
      const headerTopOffset = vw < 700 ? 16 : 40;
      const top = Math.max(headerTopOffset + headerH + 16, compactNow ? 190 : 140);
      const bottomClearance = 90;
      let h = w * RATIO;
      const maxH = Math.max(vh - top - bottomClearance, 120);
      if (h > maxH) {
        h = maxH;
        w = h / RATIO;
      }
      setDims({ w: Math.round(w), h: Math.round(h), top, vw });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [headerH]);
  const isCompact = dims.w < 700;
  // Header orientation (title left / pills right vs. stacked) must follow the
  // actual screen width, NOT `dims.w` — `dims.w` is the ratio-constrained image
  // width, which shrinks on short-but-wide viewports and would wrongly stack the
  // header on a wide desktop window that just isn't very tall.
  const isNarrow = dims.vw < 700;

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const hasLoadedCategories = useRef(false);

  const amenitiesSummary = useMemo(
    () => formatAmenitiesSummary(amenities),
    [amenities]
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setActiveCategory(null);
      if (!hasLoadedCategories.current) {
        // Pass the index explicitly: the setCurrentIndex above has only been
        // queued, so `currentIndex` inside fetchCategories is still the
        // previous render's value.
        fetchCategories(initialIndex);
      }
    }
  }, [isOpen, initialIndex]);

  // ── Data ─────────────────────────────────────────────────────────────────

  const fetchCategories = async (fromIndex: number) => {
    try {
      const response = await axios.get(
        `${base_url}/propertyImage/property_image_for_website?property_id=${propertyId}`
      );
      if (response.data?.data) {
        // Before switching, remember current image URL so we can find it in the new list
        const currentUrl = carouselImages[fromIndex];
        const newCategories: RoomCategory[] = response.data.data;
        setCategories(newCategories);
        hasLoadedCategories.current = true;

        // Find the current image in the full categorized list to preserve position
        if (currentUrl) {
          const allNew = flattenCategories(newCategories);
          const matchIdx = allNew.findIndex((img) => img.url === currentUrl);
          if (matchIdx >= 0) {
            const track = trackRef.current;
            if (track) track.style.transition = "none";
            setCurrentIndex(matchIdx);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (track) track.style.transition = "transform 0.4s ease";
              });
            });
          } else {
            setCurrentIndex(0);
          }
        }
      }
    } catch {
      // Silently fail — carouselImages fallback is already showing
    }
  };

  // Derive category titles
  const categoryTitles = useMemo<string[]>(() => {
    const seen = new Set<string>();
    return categories
      .map((c) => c.title)
      .filter((t) => {
        if (seen.has(t)) return false;
        seen.add(t);
        return true;
      });
  }, [categories]);

  // Categorized flat list (only used when a pill is active)
  const categorizedImages = useMemo<FlatImage[]>(
    () => flattenCategories(categories),
    [categories]
  );

  // The displayed images:
  // - No pill active + categories loaded → all images from gallery API
  // - No pill active + categories loading → carouselImages as fallback
  // - Pill active → that category's images only
  const filteredImages = useMemo<FlatImage[]>(() => {
    if (activeCategory) {
      return categorizedImages.filter((img) => img.roomTitle === activeCategory);
    }
    if (categorizedImages.length > 0) {
      return categorizedImages;
    }
    return carouselImages.map((url) => ({ url, roomTitle: "" }));
  }, [carouselImages, categorizedImages, activeCategory]);

  // Clamp index when filtered images change
  useEffect(() => {
    if (currentIndex >= filteredImages.length) {
      setCurrentIndex(0);
    }
  }, [filteredImages.length]);

  // ── Navigation ───────────────────────────────────────────────────────────

  const imageCount = filteredImages.length;

  // Build extended strip for infinite loop: [last, ...all, first]
  const extImages = useMemo(() => {
    if (imageCount <= 1) return filteredImages;
    return [
      filteredImages[imageCount - 1],
      ...filteredImages,
      filteredImages[0],
    ];
  }, [filteredImages, imageCount]);

  const trackIndex = imageCount > 1 ? currentIndex + 1 : 0;

  const handlePrev = useCallback(() => {
    if (isTransitioning || imageCount <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  }, [isTransitioning, imageCount]);

  const handleNext = useCallback(() => {
    if (isTransitioning || imageCount <= 1) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  }, [isTransitioning, imageCount]);

  const handleTransitionEnd = useCallback(() => {
    setIsTransitioning(false);
    if (imageCount <= 1) return;
    const track = trackRef.current;
    if (!track) return;

    if (currentIndex < 0) {
      track.style.transition = "none";
      setCurrentIndex(imageCount - 1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (track) track.style.transition = "transform 0.4s ease";
        });
      });
    } else if (currentIndex >= imageCount) {
      track.style.transition = "none";
      setCurrentIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (track) track.style.transition = "transform 0.4s ease";
        });
      });
    }
  }, [currentIndex, imageCount]);

  const handleCategoryClick = useCallback((title: string) => {
    setActiveCategory((prev) => (prev === title ? null : title));
    setCurrentIndex(0);
    // Reset transition state when switching categories
    const track = trackRef.current;
    if (track) {
      track.style.transition = "none";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (track) track.style.transition = "transform 0.4s ease";
        });
      });
    }
  }, []);

  // ── Keyboard ─────────────────────────────────────────────────────────────

  useKeypress(["ArrowLeft", "ArrowRight", "Escape"], (e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === "ArrowLeft") handlePrev();
    else if (e.key === "ArrowRight") handleNext();
    else if (e.key === "Escape") onClose();
  });

  // ── Swipe ────────────────────────────────────────────────────────────────

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  // ── Image error fallback ────────────────────────────────────────────────

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, originalUrl: string) => {
    const img = e.currentTarget;
    img.onerror = null;
    img.src = originalUrl;
  };

  // ── Background image for blur ────────────────────────────────────────────

  const bgImageUrl = filteredImages[
    Math.max(0, Math.min(currentIndex, filteredImages.length - 1))
  ]?.url;

  const bgVariantUrl = getImageVariantUrl(bgImageUrl, "_compress");

  // ── Render ───────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  // Image dimensions — width/height are responsive (see `dims` above); the gap
  // between neighbouring frames stays fixed.
  const IMG_WIDTH = dims.w;
  const IMG_HEIGHT = dims.h;
  const IMG_TOP = dims.top;
  const GAP = 20;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop. The opaque base colour matters: the blurred photo is the
            only layer that fully hides the page, and it arrives over the
            network. The teal on top of it is #01152EAD — ~68% alpha — so until
            that photo painted, the property page showed through at ~32%. */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0"
            style={{ zIndex: 50, backgroundColor: "#01152E" }}
          >
            {/* Blurred background image */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: bgVariantUrl ? `url("${bgVariantUrl}")` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(40px)",
                transform: "scale(1.1)",
              }}
            />
            {/* Teal overlay */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#01152EAD",
              }}
            />
          </div>
        </Transition.Child>

        {/* Content */}
        <div className="fixed inset-0" style={{ zIndex: 51 }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full h-full"
              style={{ position: "relative", cursor: "pointer" }}
              onClick={onClose}
            >
              {/* ── Header ──────────────────────────────────────────── */}
              <div
                ref={headerRef}
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  top: isNarrow ? 16 : 40,
                  left: isNarrow ? 16 : 40,
                  right: isNarrow ? 16 : 40,
                  display: "flex",
                  flexDirection: isNarrow ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  zIndex: 10,
                  // Larger gap on mobile gives the pills breathing room below
                  // the location line.
                  gap: isCompact ? 24 : 24,
                }}
              >
                {/* Left: Title + Location */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flexShrink: 1 }}>
                  <h2
                    style={{
                      fontFamily: "Manrope, sans-serif",
                      fontWeight: 700,
                      fontSize: isNarrow ? 20 : 26,
                      lineHeight: isNarrow ? "26px" : "32px",
                      color: "#ffffff",
                      margin: 0,
                      wordBreak: "break-word",
                    }}
                  >
                    {propertyTitle}
                  </h2>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <img
                      src={pinIconWhite}
                      alt=""
                      style={{ width: 10, height: 13, flexShrink: 0 }}
                    />
                    <span
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: "20px",
                        color: "#d6d6d6",
                      }}
                    >
                      {propertyLocation}
                    </span>
                  </div>
                </div>

                {/* Right: Category pills */}
                {categoryTitles.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: isNarrow ? 8 : 12,
                      justifyContent: "flex-start",
                      flexShrink: 0,
                      maxWidth: isNarrow ? "100%" : "50%",
                    }}
                  >
                    {categoryTitles.map((title) => (
                      <button
                        key={title}
                        onClick={() => handleCategoryClick(title)}
                        style={{
                          background: activeCategory === title
                            ? "#ffffff"
                            : "#01152E",
                          color: activeCategory === title
                            ? "#01152E"
                            : "#ffffff",
                          border: "none",
                          borderRadius: 12,
                          height: 25,
                          padding: "0 12px",
                          display: "flex",
                          alignItems: "center",
                          fontFamily: "Manrope, sans-serif",
                          fontWeight: 700,
                          fontSize: 12,
                          lineHeight: "12px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          boxShadow: "0px 0px 1px rgba(0, 0, 0, 0.25)",
                          transition: "background 0.2s, color 0.2s",
                        }}
                      >
                        {title}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Carousel ────────────────────────────────────────── */}
              <div
                {...swipeHandlers}
                onClick={(e) => e.stopPropagation()}
                style={{
                  cursor: "default",
                  position: "absolute",
                  top: IMG_TOP,
                  left: 0,
                  right: 0,
                  overflow: "hidden",
                  height: IMG_HEIGHT,
                }}
              >
                <div
                  ref={trackRef}
                  onTransitionEnd={handleTransitionEnd}
                  style={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: GAP,
                    position: "absolute",
                    top: 0,
                    height: "100%",
                    transition: "transform 0.4s ease",
                    transform: `translateX(calc(50vw - ${IMG_WIDTH / 2}px - ${trackIndex} * ${IMG_WIDTH + GAP}px))`,
                  }}
                >
                  {extImages.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        flexShrink: 0,
                        width: IMG_WIDTH,
                        height: "100%",
                        borderRadius: 10,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <img
                        src={getImageVariantUrl(item.url, "_compress")}
                        onError={(e) => handleImageError(e, item.url)}
                        alt={item.roomTitle || propertyTitle}
                        loading={idx <= 2 ? "eager" : "lazy"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {/* Room label overlay */}
                      {item.roomTitle && (
                        <div
                          style={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            // Never wider than the image; the amenities line
                            // wraps inside instead of overflowing off the right
                            // edge (was clipped on narrow/mobile frames).
                            maxWidth: "calc(100% - 24px)",
                            background: "#01152ECC",
                            borderRadius: 4,
                            padding: "8px 12px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.25)",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontWeight: 600,
                              fontSize: 10,
                              lineHeight: "12px",
                              color: "#ffffff",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.roomTitle}
                          </span>
                          <span
                            style={{
                              fontFamily: "Roboto, sans-serif",
                              fontWeight: 400,
                              fontSize: 10,
                              lineHeight: "14px",
                              color: "#d6d6d6",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.roomDescription}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                aria-label="Back to property details"
                style={{
                  position: "absolute",
                  top: IMG_TOP + IMG_HEIGHT + 24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10,
                  background: "#ffffff",
                  color: "#01152E",
                  border: "none",
                  borderRadius: 20,
                  padding: "6px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 500,
                  fontSize: 12,
                  lineHeight: "18px",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
                  outline: "none",
                }}
              >
                <svg width="6" height="11" viewBox="0 0 8 14" fill="none">
                  <path d="M7 1L1 7L7 13" stroke="#01152E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
              </button>

              {/* ── Navigation Arrows ───────────────────────────────── */}
              {imageCount > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                    aria-label="Previous image"
                    style={{
                      position: "absolute",
                      left: isCompact ? 8 : 40,
                      top: IMG_TOP + IMG_HEIGHT / 2,
                      transform: "translateY(-50%)",
                      zIndex: 10,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <img src={galleryArrowLeft} alt="" style={{ width: isCompact ? 36 : 52, height: isCompact ? 40 : 58 }} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleNext(); }}
                    aria-label="Next image"
                    style={{
                      position: "absolute",
                      right: isCompact ? 8 : 40,
                      top: IMG_TOP + IMG_HEIGHT / 2,
                      transform: "translateY(-50%)",
                      zIndex: 10,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <img src={galleryArrowRight} alt="" style={{ width: isCompact ? 36 : 52, height: isCompact ? 40 : 58 }} />
                  </button>
                </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PhotoGalleryModal;
